import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useMemo } from "react";

import { colord } from "../UserImage/color";

export type Status = "idle" | "online";

const sizes = {
  "2": "h-2 w-2 border-2",
  "3.5": "h-3.5 w-3.5 border-3",
  "5": "h-5 w-5 border-4",
};

export type StatusIndicatorSizes = keyof typeof sizes;

export interface StatusIndicatorProps {
  /** Custom color for idle status, should be a valid CSS color value */
  idleColor?: string;
  /** Custom color for online status, should be a valid CSS color value */
  onlineColor?: string;
  /** Status indicator size */
  size?: StatusIndicatorSizes;
  /** User status */
  status: Status;
}

/**
 * Small circular indicator that shows the user's online status.
 */
export const StatusIndicator = ({
  idleColor,
  onlineColor,
  size = "3.5",
  status,
}: StatusIndicatorProps) => {
  const customColor = useMemo(() => {
    const color = status === "idle" ? idleColor : onlineColor;
    return color && colord(color).isValid() ? color : undefined;
  }, [idleColor, onlineColor, status]);

  const style = useMemo(
    () => (customColor ? { backgroundColor: customColor } : undefined),
    [customColor],
  );

  const hasCustomColor = Boolean(customColor);
  const defaultColor = status === "idle" ? "bg-amber-500" : "bg-green-500";

  return (
    <div
      className={classNames(
        "border-tertiary rounded-full border-solid content-none",
        sizes[size],
        { [defaultColor]: !hasCustomColor },
      )}
      data-testid="status-indicator-element"
      style={style}
    />
  );
};

StatusIndicator.displayName = "StatusIndicator";
